// The three ways to put bundles into an update batch — one by search, every
// bundle built from a repo, every bundle live on Play. Shared by the New
// Batch modal (which stages locally) and the batch page (which adds at once).
// Busy bundles (held by another active batch) are filtered out here and
// reported to the parent, so both screens say the same thing about skips.
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getBuildRepos } from '@/api/app-builds';
import { listCandidates, type Candidate } from '@/api/update-batches';
import { Select } from '@/components/Select';

export interface PickResult {
  added: Candidate[];
  skipped: Candidate[];       // busy in another active batch
  label: string;              // "35 bundles from repo Plinko-Games"
}

type Mode = 'bundle' | 'repo' | 'published' | null;

const MODES: { id: Exclude<Mode, null>; title: string; hint: string; icon: string }[] = [
  { id: 'bundle', title: 'Add bundle', hint: 'Search and add one bundle at a time', icon: '+' },
  { id: 'repo', title: 'Add from repo', hint: 'Add every bundle built from one repo', icon: '▣' },
  { id: 'published', title: 'Add all published', hint: 'Every bundle live on the store and not banned', icon: '✓' },
];

export function BundlePicker({
  onPick, disabled = false, excludeIds,
}: {
  onPick: (r: PickResult) => void;
  disabled?: boolean;
  /** Bundles already staged/in the batch — hidden from search, not counted as added. */
  excludeIds: Set<string>;
}) {
  const [mode, setMode] = useState<Mode>(null);
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [repoId, setRepoId] = useState('');
  const [busy, setBusy] = useState(false);
  // Apps in outside publishers' accounts are rolled out by those publishers;
  // a batch of our own releases leaves them out unless asked.
  const [oursOnly, setOursOnly] = useState(true);
  // A repo usually holds apps that never reached the store; a refresh of what
  // users already have leaves those out. Off by default — the "Add all
  // published" card is the shortcut for the store-wide version of this.
  const [publishedOnly, setPublishedOnly] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  const reposQ = useQuery({ queryKey: ['build-repos'], queryFn: getBuildRepos, enabled: mode === 'repo' });
  const searchQ = useQuery({
    queryKey: ['update-batch-candidates', 'search', debounced, oursOnly, publishedOnly],
    queryFn: () => listCandidates({ source: 'search', q: debounced, ours_only: oursOnly, published_only: publishedOnly }),
    enabled: mode === 'bundle' && debounced.length >= 2,
  });

  const split = (list: Candidate[], label: string): PickResult => {
    const fresh = list.filter((c) => !excludeIds.has(c.id));
    return { added: fresh.filter((c) => !c.busy_batch), skipped: fresh.filter((c) => !!c.busy_batch), label };
  };

  const pickBulk = async (src: 'repo' | 'published') => {
    setBusy(true);
    try {
      if (src === 'repo') {
        const id = Number(repoId);
        const repo = (reposQ.data ?? []).find((r) => r.id === id);
        const list = await listCandidates({ source: 'repo', repo_id: id, ours_only: oursOnly, published_only: publishedOnly });
        const notes = [publishedOnly ? 'published only' : '', oursOnly ? 'our accounts only' : ''].filter(Boolean);
        onPick(split(list, `${list.length} bundles from repo ${repo?.name ?? id}${notes.length ? ` (${notes.join(', ')})` : ''}`));
      } else {
        const list = await listCandidates({ source: 'published', ours_only: oursOnly });
        onPick(split(list, `${list.length} bundles live on Play${oursOnly ? ' (our accounts only)' : ''}`));
      }
    } finally {
      setBusy(false);
    }
  };

  const repoOptions = useMemo(
    () => (reposQ.data ?? []).map((r) => ({ value: String(r.id), label: `${r.name} · ${r.owner}/${r.repo}` })),
    [reposQ.data],
  );
  const results = (searchQ.data ?? []).filter((c) => !excludeIds.has(c.id));

  return (
    <div className="space-y-3">
      <div className="grid gap-2 sm:grid-cols-3">
        {MODES.map((m) => (
          <button
            key={m.id}
            type="button"
            disabled={disabled}
            onClick={() => setMode(mode === m.id ? null : m.id)}
            className={`text-left rounded-lg border p-3 transition-colors disabled:opacity-50 ${
              mode === m.id ? 'border-primary bg-primary/10' : 'border-border hover:bg-surface-muted'
            }`}
          >
            <div className="text-primary text-sm leading-none mb-2">{m.icon}</div>
            <div className="text-sm font-medium text-content">{m.title}</div>
            <div className="text-xs text-content-subtle mt-0.5">{m.hint}</div>
          </button>
        ))}
      </div>

      {mode && (
        <div className="space-y-1.5">
          <label className="flex items-center gap-2 text-sm text-content-muted cursor-pointer select-none">
            <input
              type="checkbox"
              checked={oursOnly}
              disabled={disabled}
              onChange={(e) => setOursOnly(e.target.checked)}
              className="rounded border-border"
            />
            Skip apps on other publishers' accounts
            <span className="text-xs text-content-subtle">(apps flagged “Сторонний аккаунт”)</span>
          </label>
          {mode !== 'published' && (
            <label className="flex items-center gap-2 text-sm text-content-muted cursor-pointer select-none">
              <input
                type="checkbox"
                checked={publishedOnly}
                disabled={disabled}
                onChange={(e) => setPublishedOnly(e.target.checked)}
                className="rounded border-border"
              />
              Only published
              <span className="text-xs text-content-subtle">(live on Google Play and not banned)</span>
            </label>
          )}
        </div>
      )}

      {mode === 'bundle' && (
        <div className="rounded-lg border border-border p-3 space-y-2">
          <input
            autoFocus
            className="input"
            placeholder="Search by name or bundle id…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          {debounced.length < 2 ? (
            <p className="text-xs text-content-subtle">Type at least 2 characters.</p>
          ) : searchQ.isLoading ? (
            <p className="text-xs text-content-subtle">Searching…</p>
          ) : results.length === 0 ? (
            <p className="text-xs text-content-subtle">Nothing matches.</p>
          ) : (
            <ul className="max-h-56 overflow-y-auto divide-y divide-border">
              {results.map((c) => (
                <li key={c.id} className="flex items-center gap-3 py-2">
                  <AppIcon url={c.icon_url} name={c.name} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className="text-sm text-content truncate">{c.name}</span>
                      {c.external_account && (
                        <span className="shrink-0 px-1.5 py-0.5 rounded text-[11px] font-medium bg-violet-500/15 text-violet-500" title={c.publisher_name ? `Publisher: ${c.publisher_name}` : 'Outside store account'}>
                          {c.publisher_name ? `Ext · ${c.publisher_name}` : 'Ext'}
                        </span>
                      )}
                    </div>
                    <div className="text-xs font-mono text-content-subtle truncate">{c.bundle_id}</div>
                  </div>
                  {c.busy_batch ? (
                    <span className="text-xs text-warning" title={`Already in batch “${c.busy_batch.name}”`}>
                      in “{c.busy_batch.name}”
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="text-xs text-primary hover:underline"
                      onClick={() => onPick({ added: [c], skipped: [], label: c.name })}
                    >
                      Add
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {mode === 'repo' && (
        <div className="rounded-lg border border-border p-3 flex flex-col sm:flex-row gap-2 sm:items-center">
          <Select
            value={repoId}
            options={repoOptions}
            onChange={setRepoId}
            placeholder={reposQ.isLoading ? 'Loading repos…' : '— pick a repo —'}
            searchable
            className="flex-1"
          />
          <button
            type="button"
            className="btn-primary"
            disabled={!repoId || busy || disabled}
            onClick={() => pickBulk('repo')}
          >
            {busy ? 'Adding…' : 'Add all from repo'}
          </button>
        </div>
      )}

      {mode === 'published' && (
        <div className="rounded-lg border border-border p-3 flex items-center justify-between gap-3">
          <p className="text-sm text-content-muted">Adds every bundle whose production release is live on Google Play.</p>
          <button type="button" className="btn-primary shrink-0" disabled={busy || disabled} onClick={() => pickBulk('published')}>
            {busy ? 'Adding…' : 'Add all published'}
          </button>
        </div>
      )}
    </div>
  );
}

export function AppIcon({ url, name, size = 8 }: { url: string | null; name: string; size?: 8 | 10 }) {
  const cls = size === 10 ? 'w-10 h-10' : 'w-8 h-8';
  return (
    <div className={`${cls} rounded-lg overflow-hidden border border-border bg-surface-muted shrink-0`}>
      {url && (
        <img
          src={url}
          alt={name}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={(e) => { (e.target as HTMLImageElement).style.visibility = 'hidden'; }}
        />
      )}
    </div>
  );
}

/** One line for a bulk pick: what came in, what was skipped and where it sits. */
export function describePick(r: PickResult): string {
  const parts = [`Added ${r.added.length} of ${r.label}`];
  if (r.skipped.length > 0) {
    const names = [...new Set(r.skipped.map((c) => c.busy_batch!.name))];
    parts.push(`skipped ${r.skipped.length} already in ${names.map((n) => `“${n}”`).join(', ')}`);
  }
  return parts.join(' · ');
}
